import { Check, Search } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { usePendingWrite } from '@/lib/use-pending-write';
import { cn } from '@/lib/utils';
import { isStale, problemMessage, standingChanged } from '../api/problem';
import { usePeople, type PersonRow } from '../people/use-people';
import { Avatar } from '../ui/person-bits';
import { SidePanel } from '../ui/SidePanel';
import { grantPairs, type GrantOutcome } from './grant';
import { RoleChecklist } from './RoleChecklist';
import { useRoleText } from './role-text';
import {
  useAssignableRoles,
  type AssignableRole,
  type AssignableRolesState,
} from './use-assignable-roles';

type Place = {
  readonly placeKind: 'tenants' | 'organizations';
  readonly placeId: string;
  readonly placeName: string;
};

/** The set with this id in it when it was not, and out of it when it was. */
function toggled(current: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(current);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
}

function useSayOutcome(onRefresh: () => void, onStanding?: () => void) {
  const { t } = useTranslation('administration');
  return (outcome: GrantOutcome, success: string) => {
    if (outcome.failed === 0) {
      toast.success(success);
      return;
    }
    // A refusal that says the screen is stale: read again, so the panel is not refused twice.
    const body = outcome.firstFailure?.body;
    if (standingChanged(body)) {
      onStanding?.();
    }
    if (isStale(body)) {
      onRefresh();
    }
    const reason =
      outcome.firstFailure === null
        ? t('problems.generic')
        : problemMessage(t, outcome.firstFailure.body, outcome.firstFailure.status);
    toast.error(
      t('rolesPage.done.partial', { ok: outcome.granted, failed: outcome.failed, reason }),
    );
  };
}

/**
 * The many-to-many panel from the person's side (the screen standard's § Many-to-many relations):
 * the roles this caller may assign here, those the person already holds here shown as held, and
 * several chosen at once. «Concedi ruolo» on the sheet and in the «Per persona» view open it.
 */
export function GrantRolesPanel({
  open,
  onOpenChange,
  person,
  heldRoleIds,
  onGranted,
  onStanding,
  ...place
}: Place & {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly person: { readonly id: string; readonly displayName: string };
  readonly heldRoleIds: ReadonlySet<string>;
  readonly onGranted: () => void;
  readonly onStanding?: () => void;
}) {
  const { t } = useTranslation('administration');
  const { pending, run } = usePendingWrite();
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const say = useSayOutcome(onGranted, onStanding);

  useEffect(() => {
    if (!open) {
      setSelected(new Set());
    }
  }, [open]);

  const held = useMemo(
    () => new Map([...heldRoleIds].map((id) => [id, t('rolesPage.toPerson.held')] as const)),
    [heldRoleIds, t],
  );

  const toggle = (roleId: string) => setSelected((current) => toggled(current, roleId));

  const submit = () =>
    void run(async () => {
      const outcome = await grantPairs(
        place.placeKind,
        place.placeId,
        [...selected].map((roleId) => ({ userId: person.id, roleId })),
      );
      say(
        outcome,
        t('rolesPage.done.toPerson', { count: outcome.granted, name: person.displayName }),
      );
      if (outcome.granted > 0) {
        onGranted();
      }
      if (outcome.failed === 0) {
        onOpenChange(false);
      }
    });

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      title={t('rolesPage.toPerson.title', { name: person.displayName })}
      description={t('rolesPage.toPerson.summary', { place: place.placeName })}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('invite.cancel')}
          </Button>
          <Button onClick={submit} disabled={pending || selected.size === 0}>
            <Check />
            {selected.size === 0
              ? t('rolesPage.toPerson.submitNone')
              : t('rolesPage.toPerson.submit', { count: selected.size })}
          </Button>
        </>
      }
    >
      <AssignableRolesChoice
        placeKind={place.placeKind}
        placeId={place.placeId}
        selected={selected}
        onToggle={toggle}
        held={held}
        legend={t('rolesPage.toPerson.title', { name: person.displayName })}
      />
    </SidePanel>
  );
}

function RoleOptions({
  role,
  roles,
  onRetry,
  onPick,
}: {
  readonly role: AssignableRole | null;
  readonly roles: AssignableRolesState;
  readonly onRetry: () => void;
  readonly onPick: (roleId: string) => void;
}) {
  const { t } = useTranslation('administration');
  const { name } = useRoleText();
  if (roles.status === 'loading') {
    return (
      <p role="status" className="text-muted-foreground text-sm">
        {t('common.loading')}
      </p>
    );
  }
  if (roles.status === 'error') {
    return (
      <p role="alert" className="flex items-center gap-3 text-sm">
        {t('invite.rolesError')}
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      </p>
    );
  }
  if (roles.roles.length === 0) {
    return <p className="text-muted-foreground text-sm">{t('invite.noRoles')}</p>;
  }
  return (
    <div
      className="flex flex-col gap-1.5"
      role="radiogroup"
      aria-label={t('rolesPage.toPeople.chooseRole')}
    >
      {roles.roles.map((candidate) => (
        <label
          key={candidate.id}
          className={cn('role-option', role?.id === candidate.id && 'role-option-checked')}
        >
          <input
            type="radio"
            name="grant-role"
            checked={role?.id === candidate.id}
            onChange={() => onPick(candidate.id)}
          />
          <span className="role-option-name">{name(candidate)}</span>
        </label>
      ))}
    </div>
  );
}

/** The role being granted, with «Cambia» opening the list of the roles this caller may assign. */
function RoleChooser({
  role,
  roles,
  choosing,
  onToggle,
  onRetry,
  onPick,
}: {
  readonly role: AssignableRole | null;
  readonly roles: AssignableRolesState;
  readonly choosing: boolean;
  readonly onToggle: () => void;
  readonly onRetry: () => void;
  readonly onPick: (roleId: string) => void;
}) {
  const { t } = useTranslation('administration');
  const { name } = useRoleText();
  return (
    <>
      <div className="grant-role-line">
        <span className="flex min-w-0 flex-col">
          <span className="text-muted-foreground text-[12.5px]">
            {t('rolesPage.toPeople.roleLabel')}
          </span>
          <span className="text-[14.5px] font-semibold">
            {role === null ? t('rolesPage.toPeople.chooseRole') : name(role)}
          </span>
        </span>
        <Button variant="ghost" size="sm" onClick={onToggle}>
          {t('rolesPage.toPeople.change')}
        </Button>
      </div>
      {choosing || role === null ? (
        <RoleOptions role={role} roles={roles} onRetry={onRetry} onPick={onPick} />
      ) : null}
    </>
  );
}

function PeopleSearch({
  typed,
  onType,
}: {
  readonly typed: string;
  readonly onType: (value: string) => void;
}) {
  const { t } = useTranslation('administration');
  const searchId = useId();
  return (
    <div className="relative">
      <label htmlFor={searchId} className="sr-only">
        {t('rolesPage.toPeople.searchLabel')}
      </label>
      <Search
        aria-hidden="true"
        className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
      />
      <Input
        id={searchId}
        type="search"
        value={typed}
        onChange={(event) => onType(event.target.value)}
        placeholder={t('rolesPage.toPeople.searchLabel')}
        className="pl-9"
      />
    </div>
  );
}

function useGrantToPeople({
  open,
  onOpenChange,
  role,
  place,
  onGranted,
  onStanding,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly role: AssignableRole | null;
  readonly place: Place;
  readonly onGranted: () => void;
  readonly onStanding: (() => void) | undefined;
}) {
  const { t } = useTranslation('administration');
  const { name } = useRoleText();
  const { pending, run } = usePendingWrite();
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [choosingRole, setChoosingRole] = useState(false);
  const say = useSayOutcome(onGranted, onStanding);

  useEffect(() => {
    const timer = setTimeout(() => setQ(typed.trim()), 300);
    return () => clearTimeout(timer);
  }, [typed]);

  useEffect(() => {
    if (!open) {
      setSelected(new Set());
      setTyped('');
      setChoosingRole(false);
    }
  }, [open]);

  const toggle = (userId: string) => setSelected((current) => toggled(current, userId));

  const submit = () => {
    if (role === null) {
      return;
    }
    void run(async () => {
      const outcome = await grantPairs(
        place.placeKind,
        place.placeId,
        [...selected].map((userId) => ({ userId, roleId: role.id })),
      );
      say(outcome, t('rolesPage.done.toPeople', { count: outcome.granted, role: name(role) }));
      if (outcome.granted > 0) {
        onGranted();
      }
      if (outcome.failed === 0) {
        onOpenChange(false);
      }
    });
  };

  return { pending, selected, toggle, typed, setTyped, q, choosingRole, setChoosingRole, submit };
}

function PeopleGroup({
  place,
  q,
  holderIds,
  selected,
  onToggle,
}: {
  readonly place: Place;
  readonly q: string;
  readonly holderIds: ReadonlySet<string>;
  readonly selected: ReadonlySet<string>;
  readonly onToggle: (userId: string) => void;
}) {
  const { t } = useTranslation('administration');
  return (
    <div
      role="group"
      aria-label={t('rolesPage.toPeople.peopleLabel', { place: place.placeName })}
      className="flex flex-col gap-0.5"
    >
      <PeopleChoice
        placeKind={place.placeKind}
        placeId={place.placeId}
        q={q}
        holderIds={holderIds}
        selected={selected}
        onToggle={onToggle}
      />
    </div>
  );
}

function NotListed({
  placeName,
  onInvite,
}: {
  readonly placeName: string;
  readonly onInvite: () => void;
}) {
  const { t } = useTranslation('administration');
  return (
    <p className="text-muted-foreground text-[13.5px]">
      {t('rolesPage.toPeople.notListed')}{' '}
      <button
        type="button"
        className="text-link font-semibold underline underline-offset-3"
        onClick={onInvite}
      >
        {t('rolesPage.toPeople.invite', { place: placeName })}
      </button>
    </p>
  );
}

/**
 * The same panel from the role's side: one role, several people of the place. Only active people
 * are offered; someone who already holds the role here is shown, checked and disabled, so the list
 * does not look as if they were missing. Someone who is not here yet is invited instead.
 */
export function GrantToPeoplePanel({
  open,
  onOpenChange,
  role,
  roles,
  onRetryRoles,
  onRoleChange,
  holderIds,
  onGranted,
  onInvite,
  onStanding,
  ...place
}: Place & {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly role: AssignableRole | null;
  /** The page's own read of the roles this caller may assign here, with its state. */
  readonly roles: AssignableRolesState;
  readonly onRetryRoles: () => void;
  readonly onRoleChange: (roleId: string) => void;
  readonly holderIds: ReadonlySet<string>;
  readonly onGranted: () => void;
  readonly onInvite?: () => void;
  readonly onStanding?: () => void;
}) {
  const { t } = useTranslation('administration');
  const { pending, selected, toggle, typed, setTyped, q, choosingRole, setChoosingRole, submit } =
    useGrantToPeople({ open, onOpenChange, role, place, onGranted, onStanding });

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      title={t('rolesPage.toPeople.title')}
      description={t('rolesPage.toPeople.summary', { place: place.placeName })}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('invite.cancel')}
          </Button>
          <Button onClick={submit} disabled={pending || role === null || selected.size === 0}>
            <Check />
            {selected.size === 0
              ? t('rolesPage.toPeople.submitNone')
              : t('rolesPage.toPeople.submit', { count: selected.size })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <RoleChooser
          role={role}
          roles={roles}
          choosing={choosingRole}
          onToggle={() => setChoosingRole((value) => !value)}
          onRetry={onRetryRoles}
          onPick={(roleId) => {
            onRoleChange(roleId);
            setChoosingRole(false);
          }}
        />
        <PeopleSearch typed={typed} onType={setTyped} />
        <PeopleGroup
          place={place}
          q={q}
          holderIds={holderIds}
          selected={selected}
          onToggle={toggle}
        />
        {onInvite !== undefined ? (
          <NotListed placeName={place.placeName} onInvite={onInvite} />
        ) : null}
      </div>
    </SidePanel>
  );
}

/** The roles a person can be given here — read only while its panel is open. */
function AssignableRolesChoice({
  placeKind,
  placeId,
  selected,
  onToggle,
  held,
  legend,
}: {
  readonly placeKind: 'tenants' | 'organizations';
  readonly placeId: string;
  readonly selected: ReadonlySet<string>;
  readonly onToggle: (roleId: string) => void;
  readonly held: ReadonlyMap<string, string>;
  readonly legend: string;
}) {
  const { t } = useTranslation('administration');
  const { state: roles, retry } = useAssignableRoles(placeKind, placeId);
  if (roles.status === 'loading') {
    return (
      <p role="status" className="text-muted-foreground text-sm">
        {t('common.loading')}
      </p>
    );
  }
  if (roles.status === 'error') {
    return (
      <p role="alert" className="flex items-center gap-3 text-sm">
        {t('invite.rolesError')}
        <Button variant="outline" size="sm" onClick={retry}>
          {t('common.retry')}
        </Button>
      </p>
    );
  }
  if (roles.roles.length === 0) {
    return <p className="text-muted-foreground text-sm">{t('invite.noRoles')}</p>;
  }
  return (
    <RoleChecklist
      roles={roles.roles}
      selected={selected}
      onToggle={onToggle}
      held={held}
      legend={legend}
    />
  );
}

function PersonOption({
  person,
  holds,
  selected,
  onToggle,
}: {
  readonly person: PersonRow;
  readonly holds: boolean;
  readonly selected: boolean;
  readonly onToggle: (userId: string) => void;
}) {
  const { t } = useTranslation('administration');
  const checked = holds || selected;
  return (
    <label className={cn('grant-person', checked && !holds && 'grant-person-checked')}>
      <input
        type="checkbox"
        checked={checked}
        disabled={holds}
        onChange={() => onToggle(person.id)}
      />
      <Avatar name={person.displayName} muted={holds} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[14.5px] font-semibold">{person.displayName}</span>
        <span className="text-muted-foreground truncate text-[12.5px]">{person.email}</span>
      </span>
      {holds ? (
        <span className="text-muted-foreground text-[12.5px] font-semibold">
          {t('rolesPage.toPeople.alreadyHas')}
        </span>
      ) : null}
    </label>
  );
}

/** The active people of the place, searched by the server — read only while its panel is open. */
function PeopleChoice({
  placeKind,
  placeId,
  q,
  holderIds,
  selected,
  onToggle,
}: {
  readonly placeKind: 'tenants' | 'organizations';
  readonly placeId: string;
  readonly q: string;
  readonly holderIds: ReadonlySet<string>;
  readonly selected: ReadonlySet<string>;
  readonly onToggle: (userId: string) => void;
}) {
  const { t } = useTranslation('administration');
  const filters = useMemo(
    () => ({ q, roleId: null, status: 'active' as const, sort: 'name' as const }),
    [q],
  );
  const { state: people, more, reload } = usePeople(placeKind, placeId, filters);
  const persons = people.items.filter((item): item is PersonRow => item.kind === 'user');
  // A failed read is said as one, with a retry — never as a place with nobody in it.
  const failure = (
    <p role="alert" className="flex items-center gap-3 text-sm">
      {t('people.loadError')}
      <Button variant="outline" size="sm" onClick={reload}>
        {t('common.retry')}
      </Button>
    </p>
  );
  if (people.status === 'loading' && persons.length === 0) {
    return (
      <p role="status" className="text-muted-foreground text-sm">
        {t('common.loading')}
      </p>
    );
  }
  if (people.status === 'error' && persons.length === 0) {
    return failure;
  }
  if (persons.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        {q === '' ? t('rolesPage.toPeople.noPeople') : t('people.noResults.title')}
      </p>
    );
  }
  return (
    <>
      {persons.map((person) => (
        <PersonOption
          key={person.id}
          person={person}
          holds={holderIds.has(person.id)}
          selected={selected.has(person.id)}
          onToggle={onToggle}
        />
      ))}
      {people.status === 'error' ? failure : null}
      {people.status === 'ready' && people.hasMore ? (
        <Button
          variant="ghost"
          size="sm"
          className="self-start"
          onClick={more}
          disabled={people.loadingMore}
        >
          {t('common.more')}
        </Button>
      ) : null}
    </>
  );
}
